// Balanced Humanization Engine for Main App
// This system balances AI detection bypass with readability

class BalancedHumanizer {
  constructor() {
    this.synonymDatabase = this.buildReadableSynonyms();
    this.sentencePatterns = this.buildNaturalPatterns();
    this.humanQuirks = this.buildBalancedQuirks();
    this.aiPhrases = this.buildAIPhraseDatabase();
  }

  // AI phrase database focused on readability
  buildAIPhraseDatabase() {
    return {
      // High-impact AI phrases that need replacement
      'an essential symbol': ['a key symbol', 'a vital symbol', 'a central symbol'],
      'reflects Nigeria\'s rich history': ['shows Nigeria\'s rich history', 'represents Nigeria\'s rich history'],
      'culture, and aspirations': ['culture and goals', 'culture and hopes'],
      'officially adopted': ['formally adopted', 'formally chosen'],
      'military regime': ['military government', 'military rule'],
      'deeper symbolic meanings': ['special meanings', 'important meanings'],
      'linked to Nigeria\'s past': ['connected to Nigeria\'s past', 'tied to Nigeria\'s past'],
      'national identity': ['country\'s identity', 'nation\'s character'],
      'meticulously chosen': ['carefully chosen', 'thoughtfully chosen'],
      'reflect the country\'s diversity': ['show the country\'s diversity', 'represent the country\'s diversity'],
      'unity, and progress': ['unity and growth', 'unity and development'],
      'symbolizing Nigeria\'s fertile soil': ['representing Nigeria\'s rich soil', 'showing Nigeria\'s fertile land'],
      'agricultural potential': ['farming potential', 'crop-growing potential'],
      'strong defense, resilience': ['powerful defense, strength', 'robust defense, toughness'],
      'determination in the face of adversity': ['strength when facing challenges', 'courage during difficult times'],
      'dignity, strength, and': ['honor, power, and', 'pride, might, and'],
      'proud, hardworking spirit': ['proud, dedicated spirit', 'proud, committed spirit'],
      
      // Academic transitions (moderate replacement)
      'Furthermore,': ['Also,', 'Additionally,', 'What\'s more,'],
      'Moreover,': ['Also,', 'Besides,', 'In addition,'],
      'Therefore,': ['So,', 'As a result,', 'Because of this,'],
      'Consequently,': ['As a result,', 'So,', 'Because of this,'],
      'Nevertheless,': ['However,', 'But,', 'Still,'],
      'Nonetheless,': ['However,', 'But,', 'Still,'],
      'In addition,': ['Also,', 'Besides,', 'What\'s more,'],
      'In order to': ['To', 'So as to'],
      'Due to': ['Because of', 'As a result of'],
      'In terms of': ['Regarding', 'Concerning', 'When it comes to'],
      
      // Formal language (light replacement)
      'It is important to note': ['Note that', 'Remember that'],
      'It should be noted': ['Note that', 'Remember'],
      'It is worth noting': ['Note that', 'Remember'],
      'It is essential to': ['You must', 'It\'s crucial to'],
      'It is crucial to': ['You must', 'It\'s essential to'],
      'It is necessary to': ['You must', 'You need to'],
      'As mentioned earlier': ['As I said', 'As noted'],
      'As previously stated': ['As I said', 'As noted'],
      'In conclusion': ['Finally', 'To sum up'],
      'To summarize': ['In summary', 'To sum up']
    };
  }

  // Readable synonym database
  buildReadableSynonyms() {
    return {
      // Formal to readable conversions
      'utilize': ['use', 'employ', 'apply'],
      'implement': ['put in place', 'set up', 'establish'],
      'facilitate': ['help', 'make easier', 'enable'],
      'comprehensive': ['complete', 'thorough', 'detailed'],
      'significant': ['important', 'major', 'notable'],
      'demonstrate': ['show', 'prove', 'reveal'],
      'establish': ['set up', 'create', 'build'],
      'maintain': ['keep', 'preserve', 'continue'],
      'obtain': ['get', 'acquire', 'gain'],
      'provide': ['give', 'offer', 'supply'],
      'require': ['need', 'demand', 'call for'],
      'achieve': ['reach', 'accomplish', 'attain'],
      'ensure': ['make sure', 'guarantee', 'confirm'],
      'enhance': ['improve', 'boost', 'strengthen'],
      'optimize': ['improve', 'enhance', 'refine'],
      'leverage': ['use', 'utilize', 'take advantage of'],
      'robust': ['strong', 'solid', 'reliable'],
      'seamless': ['smooth', 'effortless', 'perfect'],
      'innovative': ['new', 'creative', 'original'],
      'cutting-edge': ['latest', 'advanced', 'modern'],
      
      // Academic words (moderate replacement)
      'furthermore': ['also', 'additionally', 'moreover'],
      'moreover': ['also', 'furthermore', 'in addition'],
      'therefore': ['so', 'thus', 'as a result'],
      'consequently': ['as a result', 'therefore', 'so'],
      'nevertheless': ['however', 'but', 'still'],
      'nonetheless': ['however', 'but', 'still'],
      'in addition': ['also', 'furthermore', 'moreover'],
      'in order to': ['to', 'so as to'],
      'due to': ['because of', 'as a result of'],
      'in terms of': ['regarding', 'concerning', 'about'],
      
      // Overused phrases (light replacement)
      'it is important to note': ['note that', 'remember that'],
      'it should be noted': ['note that', 'remember'],
      'it is worth noting': ['note that', 'remember'],
      'it is essential to': ['you must', 'it\'s crucial to'],
      'it is crucial to': ['you must', 'it\'s essential to'],
      'it is necessary to': ['you must', 'you need to'],
      'as mentioned earlier': ['as I said', 'as noted'],
      'as previously stated': ['as I said', 'as noted'],
      'in conclusion': ['finally', 'to sum up'],
      'to summarize': ['in summary', 'to sum up'],
      
      // Adjectives (light replacement)
      'excellent': ['great', 'outstanding', 'superb'],
      'outstanding': ['excellent', 'exceptional', 'remarkable'],
      'exceptional': ['outstanding', 'extraordinary', 'remarkable'],
      'remarkable': ['notable', 'extraordinary', 'exceptional'],
      'extraordinary': ['remarkable', 'exceptional', 'unusual'],
      'incredible': ['amazing', 'unbelievable', 'extraordinary'],
      'fantastic': ['great', 'wonderful', 'amazing'],
      'wonderful': ['great', 'fantastic', 'marvelous'],
      'marvelous': ['wonderful', 'fantastic', 'excellent'],
      'superb': ['excellent', 'outstanding', 'marvelous'],
      
      // Verbs (light replacement)
      'commence': ['start', 'begin', 'initiate'],
      'terminate': ['end', 'stop', 'finish'],
      'initiate': ['start', 'begin', 'commence'],
      'conclude': ['end', 'finish', 'complete'],
      'facilitate': ['help', 'assist', 'enable'],
      'elucidate': ['explain', 'clarify', 'illuminate'],
      'ascertain': ['find out', 'determine', 'discover'],
      'endeavor': ['try', 'attempt', 'strive'],
      'procure': ['get', 'obtain', 'acquire'],
      
      // Nouns (light replacement)
      'methodology': ['method', 'approach', 'technique'],
      'implementation': ['putting into practice', 'execution'],
      'utilization': ['use', 'usage', 'application'],
      'optimization': ['improvement', 'enhancement'],
      'maximization': ['maximizing', 'increasing'],
      'minimization': ['minimizing', 'reducing'],
      'standardization': ['standardizing', 'normalizing'],
      'customization': ['customizing', 'personalization'],
      'automation': ['automating', 'mechanization'],
      'digitization': ['digitizing', 'digital conversion']
    };
  }

  // Natural sentence patterns
  buildNaturalPatterns() {
    return {
      // Remove AI-typical structures
      'is used to': 'uses to',
      'are used to': 'use to',
      'was used to': 'used to',
      'were used to': 'used to',
      'is designed to': 'designed to',
      'are designed to': 'designed to',
      'is intended to': 'intended to',
      'are intended to': 'intended to',
      'is meant to': 'meant to',
      'are meant to': 'meant to',
      
      // Simplify complex phrases
      'in order to': 'to',
      'in the event that': 'if',
      'in the case of': 'if',
      'with regard to': 'regarding',
      'with respect to': 'regarding',
      'in relation to': 'about',
      'in connection with': 'with',
      'in accordance with': 'according to',
      'in compliance with': 'complying with',
      'in conjunction with': 'with',
      
      // Remove redundant phrases
      'a number of': 'several',
      'a variety of': 'various',
      'a range of': 'various',
      'a series of': 'several',
      'a multitude of': 'many',
      'a plethora of': 'many',
      'a myriad of': 'many',
      'an array of': 'various',
      'a host of': 'many',
      'a wealth of': 'much',
      
      // Remove redundant words
      'each and every': 'every',
      'first and foremost': 'first',
      'each individual': 'each',
      'every single': 'every',
      'completely finished': 'finished',
      'totally complete': 'complete',
      'absolutely essential': 'essential',
      'extremely important': 'very important',
      'highly significant': 'very significant',
      'very unique': 'unique'
    };
  }

  // Balanced human quirks (readable)
  buildBalancedQuirks() {
    return {
      // Natural sentence starters (moderate frequency)
      sentenceStarters: [
        'Actually,', 'Well,', 'So,', 'Now,',
        'You know,', 'I think,', 'I believe,',
        'From what I can see,', 'It seems like,',
        'To be honest,', 'Honestly,', 'Personally,',
        'In my experience,', 'Based on my experience,',
        'What I\'ve noticed is,', 'I\'ve observed that,',
        'From my perspective,', 'As far as I can tell,'
      ],
      
      // Natural transitions
      transitions: [
        'But', 'However,', 'On the other hand,', 'Meanwhile,',
        'At the same time,', 'Also,', 'Plus,', 'Additionally,',
        'What\'s more,', 'Besides,', 'Moreover,', 'Furthermore,',
        'In fact,', 'Actually,', 'Indeed,', 'Certainly,',
        'Of course,', 'Naturally,', 'Obviously,', 'Clearly,',
        'For example,', 'For instance,', 'Take for example,',
        'Let\'s say,', 'Imagine,', 'Consider,', 'Think about it,'
      ],
      
      // Human expressions (light usage)
      expressions: [
        'kind of', 'sort of', 'pretty much', 'more or less',
        'in a way', 'to some extent', 'at least', 'at most',
        'for the most part', 'by and large', 'on the whole',
        'all things considered', 'when you think about it',
        'if you ask me', 'as far as I\'m concerned'
      ],
      
      // Contractions (moderate frequency)
      contractions: {
        'do not': 'don\'t',
        'does not': 'doesn\'t',
        'did not': 'didn\'t',
        'will not': 'won\'t',
        'would not': 'wouldn\'t',
        'could not': 'couldn\'t',
        'should not': 'shouldn\'t',
        'cannot': 'can\'t',
        'is not': 'isn\'t',
        'are not': 'aren\'t',
        'was not': 'wasn\'t',
        'were not': 'weren\'t',
        'have not': 'haven\'t',
        'has not': 'hasn\'t',
        'had not': 'hadn\'t',
        'I am': 'I\'m',
        'you are': 'you\'re',
        'he is': 'he\'s',
        'she is': 'she\'s',
        'it is': 'it\'s',
        'we are': 'we\'re',
        'they are': 'they\'re',
        'I will': 'I\'ll',
        'you will': 'you\'ll',
        'he will': 'he\'ll',
        'she will': 'she\'ll',
        'it will': 'it\'ll',
        'we will': 'we\'ll',
        'they will': 'they\'ll',
        'I have': 'I\'ve',
        'you have': 'you\'ve',
        'he has': 'he\'s',
        'she has': 'she\'s',
        'it has': 'it\'s',
        'we have': 'we\'ve',
        'they have': 'they\'ve'
      }
    };
  }

  // Main balanced humanization function
  humanize(text) {
    if (!text || typeof text !== 'string') return text;
    
    let humanizedText = text;
    
    // Step 1: Remove specific AI phrases (high impact, low readability cost)
    humanizedText = this.removeAIPhrases(humanizedText);
    
    // Step 2: Replace AI-typical words with readable alternatives
    humanizedText = this.replaceAIWords(humanizedText);
    
    // Step 3: Restructure sentences to be more natural
    humanizedText = this.restructureSentences(humanizedText);
    
    // Step 4: Add contractions (moderate frequency for readability)
    humanizedText = this.addContractions(humanizedText);
    
    // Step 5: Add natural sentence starters (low frequency)
    humanizedText = this.addNaturalStarters(humanizedText);
    
    // Step 6: Add human expressions (very low frequency)
    humanizedText = this.addHumanExpressions(humanizedText);
    
    // Step 7: Vary sentence structure (maintain readability)
    humanizedText = this.varySentenceStructure(humanizedText);
    
    // Step 8: Clean up any readability issues
    humanizedText = this.cleanupReadability(humanizedText);
    
    return humanizedText;
  }

  // Remove specific AI phrases that detectors look for
  removeAIPhrases(text) {
    let result = text;
    
    Object.keys(this.aiPhrases).forEach(phrase => {
      const alternatives = this.aiPhrases[phrase];
      const regex = new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      
      result = result.replace(regex, (match) => {
        // Randomly choose an alternative
        const alternative = alternatives[Math.floor(Math.random() * alternatives.length)];
        return match.charAt(0) === match.charAt(0).toUpperCase() 
          ? alternative.charAt(0).toUpperCase() + alternative.slice(1)
          : alternative;
      });
    });
    
    return result;
  }

  // Replace AI-typical words with readable alternatives
  replaceAIWords(text) {
    let result = text;
    
    Object.keys(this.synonymDatabase).forEach(word => {
      const synonyms = this.synonymDatabase[word];
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      
      result = result.replace(regex, (match) => {
        // Randomly choose a synonym
        const synonym = synonyms[Math.floor(Math.random() * synonyms.length)];
        return match.charAt(0) === match.charAt(0).toUpperCase() 
          ? synonym.charAt(0).toUpperCase() + synonym.slice(1)
          : synonym;
      });
    });
    
    return result;
  }

  // Restructure sentences to be more natural
  restructureSentences(text) {
    let result = text;
    
    Object.keys(this.sentencePatterns).forEach(pattern => {
      const replacement = this.sentencePatterns[pattern];
      const regex = new RegExp(pattern, 'gi');
      result = result.replace(regex, replacement);
    });
    
    return result;
  }

  // Add contractions (moderate frequency for readability)
  addContractions(text) {
    let result = text;
    
    Object.keys(this.humanQuirks.contractions).forEach(fullForm => {
      const contraction = this.humanQuirks.contractions[fullForm];
      const regex = new RegExp(`\\b${fullForm}\\b`, 'gi');
      
      result = result.replace(regex, (match) => {
        // 60% chance to use contraction (balanced for readability)
        if (Math.random() < 0.6) {
          return match.charAt(0) === match.charAt(0).toUpperCase() 
            ? contraction.charAt(0).toUpperCase() + contraction.slice(1)
            : contraction;
        }
        return match;
      });
    });
    
    return result;
  }

  // Add natural sentence starters (low frequency)
  addNaturalStarters(text) {
    const sentences = text.split(/[.!?]+/);
    let result = sentences.map((sentence, index) => {
      if (sentence.trim().length > 25 && Math.random() < 0.1) {
        const starter = this.humanQuirks.sentenceStarters[
          Math.floor(Math.random() * this.humanQuirks.sentenceStarters.length)
        ];
        return `${starter} ${sentence.trim()}`;
      }
      return sentence;
    });
    
    return result.join('. ').replace(/\.\s*\./g, '.');
  }

  // Add human expressions (very low frequency)
  addHumanExpressions(text) {
    let result = text;
    
    // Add expressions occasionally
    this.humanQuirks.expressions.forEach(expression => {
      if (Math.random() < 0.05) {
        const sentences = result.split(/[.!?]+/);
        if (sentences.length > 1) {
          const randomIndex = Math.floor(Math.random() * (sentences.length - 1));
          sentences[randomIndex] = sentences[randomIndex].trim() + ` ${expression}`;
          result = sentences.join('. ').replace(/\.\s*\./g, '.');
        }
      }
    });
    
    return result;
  }

  // Vary sentence structure (maintain readability)
  varySentenceStructure(text) {
    const sentences = text.split(/[.!?]+/);
    let result = sentences.map(sentence => {
      if (sentence.trim().length > 60 && Math.random() < 0.2) {
        // Split long sentences occasionally
        const words = sentence.trim().split(' ');
        if (words.length > 20) {
          const splitPoint = Math.floor(words.length / 2);
          const firstPart = words.slice(0, splitPoint).join(' ');
          const secondPart = words.slice(splitPoint).join(' ');
          return `${firstPart}. ${secondPart}`;
        }
      }
      return sentence;
    });
    
    return result.join('. ').replace(/\.\s*\./g, '.');
  }

  // Clean up readability issues
  cleanupReadability(text) {
    let result = text;
    
    // Remove excessive punctuation
    result = result.replace(/\.\.\./g, '.');
    result = result.replace(/!!/g, '!');
    result = result.replace(/\?\?/g, '?');
    
    // Fix spacing issues
    result = result.replace(/\s+/g, ' ');
    result = result.replace(/\s+([.!?])/g, '$1');
    
    // Remove excessive human expressions
    result = result.replace(/(\w+)\s+kind of\s+kind of/g, '$1 kind of');
    result = result.replace(/(\w+)\s+sort of\s+sort of/g, '$1 sort of');
    
    return result.trim();
  }
}

export default BalancedHumanizer;
